import type { ComponentPropsWithoutRef } from 'react'

/**
 * Heading components — single source of truth for the YADA type scale.
 *
 * Phase 1 mobile overhaul (2026-10-13). All h1–h6 already get
 * `font-display` + `font-bold` + `text-ink` + `line-height: 1.25` from
 * globals.css (`:where(h1, h2, h3, h4, h5, h6) { … }`). The components
 * below add ONLY the responsive size + the role-specific leading.
 *
 * Use:
 *   <HeroTitle>…</HeroTitle>          — Home hero / About manifesto / 404
 *   <H1>…</H1>                        — content-page top heading
 *   <H2>…</H2>                        — section title (replaces
 *                                       `.section-title__title` clamp)
 *   <H3>…</H3>                        — card title
 *   <H4>…</H4>                        — sub-card / pricing step
 *   <Lede>…</Lede>                    — first paragraph under a heading
 *
 * The .hero-title global class (in globals.css) provides
 * `letter-spacing: -0.01em; line-height: 1.1;` — only <HeroTitle> needs it.
 */

type H1Props = ComponentPropsWithoutRef<'h1'>
type H2Props = ComponentPropsWithoutRef<'h2'>
type H3Props = ComponentPropsWithoutRef<'h3'>
type H4Props = ComponentPropsWithoutRef<'h4'>
type PProps = ComponentPropsWithoutRef<'p'>

/** Page hero / large statement heading. 30 / 36 / 48 px. */
export function HeroTitle({ className = '', ...rest }: H1Props) {
  return (
    <h1
      className={['hero-title text-3xl md:text-4xl lg:text-5xl', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  )
}

/** Top-of-page heading on a content page. 30 / 36 px. */
export function H1({ className = '', ...rest }: H1Props) {
  return (
    <h1
      className={['text-3xl md:text-4xl leading-tight', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  )
}

/** Section heading. 24 / 30 px. Replaces the dead `.section-title__title`
 *  clamp(1.875rem, 4vw, 2.75rem) rule — that was overridden on top of by
 *  Tailwind utilities at every call site, so its existence was misleading. */
export function H2({ className = '', ...rest }: H2Props) {
  return (
    <h2
      className={['text-2xl md:text-3xl leading-tight', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  )
}

/** Card heading. 18 / 20 px. Pairs with Card's `density="compact"`. */
export function H3({ className = '', ...rest }: H3Props) {
  return (
    <h3
      className={['text-lg md:text-xl leading-snug', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  )
}

/** Sub-card / step / feature heading. 16 / 18 px. */
export function H4({ className = '', ...rest }: H4Props) {
  return (
    <h4
      className={['text-base md:text-lg leading-snug', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  )
}

/** First paragraph under a heading. 16 / 18 px, soft ink, relaxed. */
export function Lede({ className = '', ...rest }: PProps) {
  return (
    <p
      className={['text-base md:text-lg text-ink-soft leading-relaxed', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  )
}
